import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import clsx from "clsx";
import { bindActionCreators, Dispatch } from "redux";

import { useLocalization, TranslatedText } from "@plex/culture-react";
import { DataTableDef, SelectionMode } from "@plex/react-components";

import { commonMessages } from "infrastructure/localization";
import { InlineEditProvider, InlineEdit } from "ui/components";
import { useLocalizedValues, useUserContext } from "ui/hooks";
import { glossaryMessages } from "ui/screens/plex-dictionary/plex-dictionary.localization";

import { translationsManagerActions } from "../TranslationsManager.actions";
import { ITermTranslationRow, ITranslationsManagerState, IChangeText, ValidationTags } from "../TranslationsManager.state";

import styles from "./TranslationsManagerGrid.module.scss";

export interface ITranslationsManagerGridProps {
  translations: ITermTranslationRow[];
  selected: ITermTranslationRow[];
  isSearched: boolean;
  changeTranslation: (change: IChangeText) => void;
  changeTranslationDefinition: (change: IChangeText) => void;
  selectTermTranslations: (selected: ITermTranslationRow[]) => void;
}

export const TranslationsManagerGrid: React.FC<ITranslationsManagerGridProps> = ({
  translations,
  selected,
  isSearched,
  changeTranslation,
  changeTranslationDefinition,
  selectTermTranslations
}) => {
  const { t } = useLocalization();
  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();

  const localizedValues = useLocalizedValues(() => {
    return {
      key: glossaryMessages.property("key"),
      sourceTerm: glossaryMessages.property("sourceTerm"),
      sourceDescription: glossaryMessages.property("sourceDescription"),
      translatedTerm: glossaryMessages.property("translatedTerm"),
      translatedDescription: glossaryMessages.property("translatedDescription"),
      translationSource: glossaryMessages.property("translationSource"),
      author: glossaryMessages.property("author"),
      status: glossaryMessages.property("status"),
      refineSearch: commonMessages.info("refineSearch"),
      startSearch: commonMessages.info("startSearch")
    };
  });
  const inReviewStatus = glossaryMessages.option("termTranslationStatusInReview").value;

  const keySelector = useCallback((row: ITermTranslationRow) => row.data.termId, []);

  const [authorizationState] = useState(() => ({
    userId: userContext.getUserId()
  }));

  const isEditable = useCallback(
    (row: ITermTranslationRow) => {
      if (roles.PlexLocalizationManager && roles.Translator) {
        return true;
      }

      if (roles.PlexLocalizationManager) {
        return row.data.translationStatus === inReviewStatus;
      }

      if (roles.Translator) {
        return row.data.translationStatus !== inReviewStatus || (row.data.author && row.data.author.toLowerCase() === authorizationState.userId) || false;
      }

      return false;
    },
    [inReviewStatus, roles, authorizationState.userId]
  );

  const translationValidationTag = [ValidationTags.Translation, ValidationTags.Both];
  const descriptionValidationTag = [ValidationTags.Description, ValidationTags.Both];

  return (
    <InlineEditProvider>
      <DataTableDef<ITermTranslationRow>
        className={clsx(styles.base, roles.PlexLocalizationManager ? styles.multipleRowSelection : styles.singleRowSelection)}
        data={translations}
        emptyText={isSearched ? localizedValues.refineSearch : localizedValues.startSearch}
        keySelector={keySelector}
        onSelectionChanged={selectTermTranslations}
        selected={selected}
        selectionMode={roles.PlexLocalizationManager ? SelectionMode.multiple : SelectionMode.single}
      >
        <DataTableDef.Column<ITermTranslationRow> id="term" title={localizedValues.sourceTerm} valueSelector={r => r.data.term} sortable className={styles.columnWidthSmall}>
          {row => <p className={styles.wrapText}>{row.data.term}</p>}
        </DataTableDef.Column>
        <DataTableDef.Column<ITermTranslationRow> id="term-source-definition" title={localizedValues.sourceDescription} valueSelector={r => r.data.definition} sortable>
          {row => <p className={styles.wrapText}>{row.data.definition}</p>}
        </DataTableDef.Column>
        <DataTableDef.Column<ITermTranslationRow> id="term-translation" title={localizedValues.translatedTerm} valueSelector={r => r.data.translation} sortable>
          {row => (
            <InlineEdit<ITermTranslationRow>
              initialValue={row.data.translation}
              originalValue={row.originalData && row.originalData.translation}
              onValueChanged={val => changeTranslation({ text: val, index: row.index })}
              isChanged={row.isTranslationChanged}
              className={styles.inlineEdit}
              validationIssues={row.validationIssues}
              validationTags={translationValidationTag}
              canEdit={isEditable}
              context={row}
            >
              <p className={styles.wrapText}>{row.data.translation}</p>
            </InlineEdit>
          )}
        </DataTableDef.Column>
        <DataTableDef.Column<ITermTranslationRow>
          id="term-translation-definition"
          title={localizedValues.translatedDescription}
          valueSelector={r => r.data.translationDefinition}
          sortable
        >
          {row => (
            <InlineEdit<ITermTranslationRow>
              initialValue={row.data.translationDefinition}
              originalValue={row.originalData && row.originalData.translationDefinition}
              onValueChanged={val => changeTranslationDefinition({ text: val, index: row.index })}
              isChanged={row.isTranslationDefinitionChanged}
              className={styles.inlineEdit}
              validationIssues={row.validationIssues}
              validationTags={descriptionValidationTag}
              canEdit={isEditable}
              context={row}
            >
              <p className={styles.wrapText}>{row.data.translationDefinition}</p>
            </InlineEdit>
          )}
        </DataTableDef.Column>
        <DataTableDef.Column<ITermTranslationRow>
          id="term-translation-source"
          title={localizedValues.translationSource}
          valueSelector={r => r.data.translationSource && t(r.data.translationSource)}
          sortable
        >
          {row => row.data.translationSource && <TranslatedText messageId={row.data.translationSource} />}
        </DataTableDef.Column>
        <DataTableDef.Column<ITermTranslationRow>
          id="term-translation-status"
          title={localizedValues.status}
          valueSelector={r => r.data.translationStatus && t(r.data.translationStatus)}
          sortable
          className={styles.columnWidthSmall}
        >
          {row => row.data.translationStatus && <TranslatedText messageId={row.data.translationStatus} />}
        </DataTableDef.Column>
        <DataTableDef.Column<ITermTranslationRow> id="7" title={localizedValues.author} valueSelector={r => r.data.authorName} sortable className={styles.columnWidthSmall}>
          {row => <p className={styles.wrapText}>{row.data.authorName}</p>}
        </DataTableDef.Column>
      </DataTableDef>
    </InlineEditProvider>
  );
};

/* istanbul ignore next */
export default connect(
  (state: { translationsManager: ITranslationsManagerState }) => {
    const grid = state.translationsManager.grid;
    return {
      translations: grid.gridData,
      selected: grid.selected,
      isSearched: grid.isSearched
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        changeTranslation: translationsManagerActions.changeTranslation,
        changeTranslationDefinition: translationsManagerActions.changeTranslationDefinition,
        selectTermTranslations: translationsManagerActions.selectTermTranslations
      },
      dispatch
    );
  }
)(TranslationsManagerGrid);
